import React, { useState, useEffect, useCallback } from "react";
import Template from "../../components/Template";
import patrimonioService from "../../services/patrimonioService";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import { useNavigate } from "react-router-dom";
import usePermission from "../../hooks/usePermission";
import { TIPOS_PATRIMONIO, getTipoLabel } from "./patrimonioConstants";

const GerenciarCategorias = () => {
  const navigate = useNavigate();

  const [categorias, setCategorias] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const podeGerenciar = usePermission("GERENCIAR_CATEGORIAS_PATRIMONIO");

  const [formData, setFormData] = useState({
    nome: "",
    tipo: "EQUIPAMENTO",
    ativo: true,
  });

  // Modais
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [categoriaToDelete, setCategoriaToDelete] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const fetchCategorias = useCallback(async () => {
    try {
      const data = await patrimonioService.listarCategorias();
      setCategorias(data);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setAlertMessage("Erro ao carregar categorias de patrimônio.");
    }
  }, []);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const limparForm = () => {
    setFormData({ nome: "", tipo: "EQUIPAMENTO", ativo: true });
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    try {
      if (isEditMode && editingId) {
        await patrimonioService.atualizarCategoria(editingId, {
          nome: formData.nome,
          tipo: formData.tipo,
          ativo: formData.ativo,
        });
      } else {
        await patrimonioService.criarCategoria({
          nome: formData.nome,
          tipo: formData.tipo,
        });
      }
      setShowSuccessDialog(true);
      fetchCategorias();
      limparForm();
    } catch (error) {
      console.error("Erro ao salvar categoria:", error);
      setAlertMessage(error.response?.data?.error || "Erro ao salvar categoria. Verifique e tente novamente.");
    }
  };

  const handleEdit = (categoria) => {
    setIsEditMode(true);
    setEditingId(categoria.id);
    setFormData({
      nome: categoria.nome,
      tipo: categoria.tipo,
      ativo: categoria.ativo,
    });
  };

  const handleDelete = (categoria) => {
    setCategoriaToDelete(categoria);
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmDelete(false);
    try {
      if (categoriaToDelete) {
        await patrimonioService.excluirCategoria(categoriaToDelete.id);
        fetchCategorias();
      }
      setCategoriaToDelete(null);
    } catch (error) {
      console.error("Erro ao excluir categoria:", error);
      setAlertMessage(error.response?.data?.error || "Erro ao excluir categoria.");
      setCategoriaToDelete(null);
    }
  };

  const categoriasFiltradas = filtroTipo
    ? categorias.filter((c) => c.tipo === filtroTipo)
    : categorias;

  return (
    <Template>
      <div className="w-full px-4 py-6 flex flex-col gap-8">
        {/* Título */}
        <h1 className="text-2xl text-stone-800 font-medium">
          Categorias de Patrimônio
        </h1>

        {/* Formulário de Criação/Edição */}
        {podeGerenciar && (
          <form onSubmit={handleSubmitForm} className="flex flex-col gap-4 bg-white p-4 rounded-md border border-slate-200">
            <div className="text-stone-600 text-base font-medium">
              {isEditMode ? "Editar Categoria" : "Cadastrar Nova Categoria"}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Tipo */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-slate-700 font-medium">Tipo</label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                >
                  {TIPOS_PATRIMONIO.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Nome */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-slate-700 font-medium">Nome da categoria</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                  placeholder="Ex: Notebook, Carro, Casa..."
                  required
                />
              </div>

              {/* Situação (apenas na edição) */}
              {isEditMode && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-slate-700 font-medium">Situação</label>
                  <label className="inline-flex items-center gap-2 text-sm mt-2">
                    <input
                      type="checkbox"
                      name="ativo"
                      checked={formData.ativo}
                      onChange={handleInputChange}
                    />
                    <span>{formData.ativo ? "Ativa" : "Inativa"}</span>
                  </label>
                </div>
              )}
            </div>

            {/* Botões */}
            <div className="flex items-center gap-4 mt-2">
              {isEditMode && (
                <button
                  type="button"
                  onClick={limparForm}
                  className="px-4 py-2 bg-stone-400 text-white text-sm font-medium rounded-md"
                >
                  Cancelar
                </button>
              )}

              <button
                type="submit"
                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
              >
                {isEditMode ? "Salvar Alterações" : "Adicionar Categoria"}
              </button>
            </div>
          </form>
        )}

        {/* Listagem */}
        <div className="bg-white p-4 rounded-md border border-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-stone-600 text-base font-medium">
              Lista de Categorias
            </div>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              <option value="">Todos os tipos</option>
              {TIPOS_PATRIMONIO.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-100">
                  <th className="px-4 py-2 border border-slate-200">Tipo</th>
                  <th className="px-4 py-2 border border-slate-200">Categoria</th>
                  <th className="px-4 py-2 border border-slate-200">Situação</th>
                  <th className="px-4 py-2 border border-slate-200">Bens</th>
                  <th className="px-4 py-2 border border-slate-200">Ações</th>
                </tr>
              </thead>
              <tbody>
                {categoriasFiltradas.length > 0 ? (
                  categoriasFiltradas.map((categoria) => (
                    <tr key={categoria.id} className="border-b">
                      <td className="px-4 py-2 border border-slate-200">{getTipoLabel(categoria.tipo)}</td>
                      <td className="px-4 py-2 border border-slate-200">{categoria.nome}</td>
                      <td className="px-4 py-2 border border-slate-200">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            categoria.ativo ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-600"
                          }`}
                        >
                          {categoria.ativo ? "Ativa" : "Inativa"}
                        </span>
                      </td>
                      <td className="px-4 py-2 border border-slate-200">
                        {categoria._count?.patrimonios ?? 0}
                      </td>
                      <td className="px-4 py-2 border border-slate-200">
                        {podeGerenciar && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleEdit(categoria)}
                              className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm mr-2"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(categoria)}
                              className="px-2 py-1 bg-red-600 text-white rounded-md text-sm"
                            >
                              Excluir
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-center text-slate-700">
                      Nenhuma categoria cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Voltar */}
        <div className="flex items-center w-full gap-4">
          <div className="flex gap-4 ml-auto">
            <button
              type="button"
              className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
              onClick={() => navigate("/patrimonios")}
            >
              <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isVisible={showConfirmDialog}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        message="Deseja salvar esta categoria?"
      />

      <ConfirmationModal
        isVisible={showConfirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowConfirmDelete(false);
          setCategoriaToDelete(null);
        }}
        message="Deseja apagar esta categoria?"
        textButtonConfirm="Sim"
      />

      <SuccessModal
        isVisible={showSuccessDialog}
        message="Categoria salva com sucesso!"
        onClose={() => setShowSuccessDialog(false)}
      />

      <AlertDialog
        isVisible={!!alertMessage}
        message={alertMessage}
        onClose={() => setAlertMessage("")}
      />
    </Template>
  );
};

export default GerenciarCategorias;
